import React, {useState} from 'react';
import {I18n} from 'react-redux-i18n';

import {
  CardActions,
  CardContent,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import {CustomButton} from '../../material-dashboard-pro-react/components';

import GenerateTOTPSecretForm from './GenerateTOTPSecretForm';
import RenderOrEmpty from '../RenderOrEmpty/RenderOrEmpty';
import ValidateTOTPSecretForm from './ValidateTOTPSecretForm';
import DownloadTOTPApp from './DownloadTOTPApp';

import ShowTOTPRecoveryCode from './ShowTOTPRecoveryCode';
import {GeneratedTOTPSecretResult} from '../../types/TOTP';

import style from './style';

type SetupTOTPFormProps = WithStyles<typeof style> & {
  isInitial?: boolean;
  // eslint-disable-next-line no-unused-vars
  onSuccess?: (responseData: { [key: string]: string | number | boolean }) => void;
};

/**
 * @param isInitial - if true, use first-time setup with authentication. Optional
 */
function SetupTOTPForm({
  classes,
  isInitial = false,
  onSuccess = () => { },
}: SetupTOTPFormProps) {
  const [validateAction, setValidateAction] = useState(() => { });
  const [activeStep, setActiveStep] = useState(0);

  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [isRecoveryCodeSaved, setIsRecoveryCodeSaved] = useState(false);
  const [generatedSecret, setGeneratedSecret] = useState<GeneratedTOTPSecretResult | undefined>();

  const [validationData, setValidationData] =
    useState<{ [key: string]: string | number | boolean }>({ });

  const steps = [
    {
      content: <DownloadTOTPApp
        isAppInstalled={isAppInstalled}
        setIsAppInstalled={setIsAppInstalled}
      />,
      blockContinue: !isAppInstalled,
    },
    {
      content: <GenerateTOTPSecretForm
        isInitial={isInitial}
        generatedSecret={generatedSecret}
        onSecretGenerated={setGeneratedSecret}
      />,
    },
    {
      content: <ValidateTOTPSecretForm
        isInitial={isInitial}
        setValidateAction={(action) => setValidateAction(action)}
        onSuccess={(data) => {
          setValidationData(data);
          setActiveStep(activeStep + 1);
        }}
      />,
      onContinueAction: validateAction,
      continueText: I18n.t('totp.setup.confirm'),
    },
    {
      content: <ShowTOTPRecoveryCode
        recoveryCode={generatedSecret?.recoveryCode ?? ''}
        isRecoveryCodeSaved={isRecoveryCodeSaved}
        setIsRecoveryCodeSaved={setIsRecoveryCodeSaved}
      />,
      blockContinue: !isRecoveryCodeSaved,
      onContinueAction: (() => onSuccess(validationData)),
      continueText: I18n.t('totp.setup.confirm'),
    },
  ];

  return (
    <div className={classes.container}>
      <CardContent>
        {steps[activeStep]?.content ?? null}
      </CardContent>

      <CardActions className={classes.container__actions}>
        <RenderOrEmpty isShow={activeStep > 0}>
          <CustomButton
            customClasses="uppercase"
            onClick={() => setActiveStep(activeStep - 1)}
          >
            {I18n.t('totp.setup.back')}
          </CustomButton>
        </RenderOrEmpty>
        <CustomButton
          disabled={!!steps[activeStep]?.blockContinue}
          color="secondary"
          customClasses="uppercase"
          onClick={
            steps[activeStep]?.onContinueAction ?? (() => setActiveStep(activeStep + 1))
          }
        >
          {steps[activeStep]?.continueText ?? I18n.t('totp.setup.continue')}
        </CustomButton>
      </CardActions>
    </div>
  );
}

export default withStyles(style)(SetupTOTPForm);
